/**
 * One shared copy of the nav tree, for every consumer.
 *
 * Why this exists: <Sidebar> lives in AppLayout, OUTSIDE the router <Outlet>, so it never
 * unmounts as you navigate. It used to fetch /api/tree once on mount and hold that copy
 * forever — which is why renaming a page in the editor left the OLD title in the sidebar
 * until a hard reload. The title was always correct in Cosmos; the sidebar just never
 * asked again. <Breadcrumbs> had the same bug from its own second, independent fetch.
 *
 * So: one store, and the writers invalidate it.
 *   - patchNode() — local-only metadata fix-up (title/status). No round-trip; the sidebar
 *     updates on the keystroke's save, which is the whole point.
 *   - applyMove() — optimistic re-parent/re-order so a drag lands under the cursor instead
 *     of snapping back for a round-trip. refresh() reconciles it (and reverts it, if the
 *     server said no).
 *   - refresh()   — authoritative re-fetch, for structural changes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '@/lib/api';
import type { Tree, TreeNode } from '@/lib/types';

interface TreeContextValue {
  tree: Tree | null;
  loading: boolean;
  error: string | null;
  /** Re-fetch from the server. The source of truth for structure. */
  refresh: () => Promise<void>;
  /** Update a node's title/status in the local tree — no network. */
  patchNode: (id: string, patch: Partial<Pick<TreeNode, 'title' | 'status'>>) => void;
  /** Optimistically re-parent `id` (into `sectionId`) and re-index `order` (the
   *  destination's children). Descendants' sectionIds are reconciled by refresh(). */
  applyMove: (id: string, parentId: string | null, sectionId: string, order: string[]) => void;
  /** Optimistically re-order sections; ids not in `order` keep their position. */
  applySectionOrder: (order: string[]) => void;
}

const TreeContext = createContext<TreeContextValue | undefined>(undefined);

export function TreeProvider({ children }: { children: ReactNode }) {
  const [tree, setTree] = useState<Tree | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setTree(await api.tree());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tree');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const patchNode = useCallback<TreeContextValue['patchNode']>((id, patch) => {
    setTree((prev) => {
      if (!prev) return prev;
      const cur = prev.nodes.find((n) => n.id === id);
      // No-op guard: autosave fires on a timer, and a new object identity here would
      // re-render the whole tree on every tick even when nothing changed.
      if (!cur) return prev;
      const changed = (Object.keys(patch) as Array<keyof typeof patch>).some(
        (k) => patch[k] !== cur[k],
      );
      if (!changed) return prev;
      return {
        ...prev,
        nodes: prev.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
      };
    });
  }, []);

  const applyMove = useCallback<TreeContextValue['applyMove']>((id, parentId, sectionId, order) => {
    setTree((prev) => {
      if (!prev) return prev;
      const posById = new Map(order.map((sid, i) => [sid, i]));
      return {
        ...prev,
        nodes: prev.nodes.map((n) => {
          const pos = posById.get(n.id);
          if (n.id === id) return { ...n, parentId, sectionId, position: pos ?? n.position };
          return pos === undefined ? n : { ...n, position: pos };
        }),
      };
    });
  }, []);

  const applySectionOrder = useCallback<TreeContextValue['applySectionOrder']>((order) => {
    setTree((prev) => {
      if (!prev) return prev;
      const posById = new Map(order.map((sid, i) => [sid, i]));
      return {
        ...prev,
        sections: prev.sections.map((s) => {
          const pos = posById.get(s.id);
          return pos === undefined ? s : { ...s, position: pos };
        }),
      };
    });
  }, []);

  const value = useMemo<TreeContextValue>(
    () => ({ tree, loading, error, refresh, patchNode, applyMove, applySectionOrder }),
    [tree, loading, error, refresh, patchNode, applyMove, applySectionOrder],
  );

  return <TreeContext.Provider value={value}>{children}</TreeContext.Provider>;
}

export function useTree(): TreeContextValue {
  const ctx = useContext(TreeContext);
  if (!ctx) throw new Error('useTree must be used within <TreeProvider>');
  return ctx;
}
